import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, FileUser, Mail, UserCog, Smartphone, LayoutDashboard, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from "@/components/ui/PaginationWrapper";
import { userService } from '../../_services';

function ManageWholesaler({
    text,
    closePopup,
    closeAddWholesalerPopup,
    closeEditProfilePopup,
    manageGroupPopup,
    manageEmailPopup,
    backSearch,
    backActivePagin
}) {
    const [loading, setLoading] = useState(true);
    const [terminatedLoading, setTerminatedLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [terminatedList, setTerminatedList] = useState([]);
    const [terminatedListTemp, setTerminatedListTemp] = useState([]);
    const [tMainAPIResult, setTMainAPIResult] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [currentPaging, setCurrentPaging] = useState(1);
    const [tabSelected, setTabSelected] = useState('Active');
    const [whSearchText, setWhSearchText] = useState(backSearch || '');
    const [error, setError] = useState('');
    const [dataNotFound, setDataNotFound] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchDataLocally = useCallback(async (roleName, roleType) => {
        try {
            const apidata = await userService.Admin_GetWholesalersList(roleName, roleType);
            
            if (apidata.length > 0) {
                setMainAPIResult(apidata);
                
                let dataToDisplay = apidata;
                
                // Apply search filter if backSearch exists
                if (backSearch) {
                    dataToDisplay = apidata.filter(e1 =>
                        e1.whName?.toLowerCase()?.indexOf(backSearch?.toLowerCase()) !== -1 ||
                        (e1.emailId && e1.emailId?.toLowerCase()?.indexOf(backSearch?.toLowerCase()) !== -1) ||
                        (e1.gnfix && e1.gnfix?.toLowerCase()?.indexOf(backSearch?.toLowerCase()) !== -1)
                    );
                    
                    if (dataToDisplay.length === 0) {
                        setDataNotFound('No records found');
                    } else {
                        setDataNotFound('');
                    }
                }
                
                setUserList(dataToDisplay);
                
                // Handle pagination restoration with filtered data
                if (backActivePagin > 1 && dataToDisplay.length > 9) {
                    // Calculate the correct page data
                    let userlistobj = JSON.parse(JSON.stringify(dataToDisplay));
                    let usertemplistobj = userlistobj.splice((backActivePagin - 1) * 9, 9);
                    setUserListTemp(usertemplistobj);
                    setActivePage(backActivePagin);
                    setCurrentPaging(backActivePagin);
                } else {
                    // First page or less than 9 items
                    if (dataToDisplay.length <= 9) {
                        setUserListTemp(dataToDisplay);
                    } else {
                        let userlistobj = JSON.parse(JSON.stringify(dataToDisplay));
                        let usertemplistobj = userlistobj.splice(0, 9);
                        setUserListTemp(usertemplistobj);
                    }
                }
            } else {
                setDataNotFound('No wholesaler found');
            }
            setLoading(false);
        } catch (error) {
            setError('Error fetching active list! Please try again.');
            setLoading(false);
        }
    }, [backActivePagin, backSearch]);

    const fetchTerminatedList = useCallback(async (id, whName) => {
        try {
            setTerminatedLoading(true);
            const apidata = await userService.Admin_GetWhTerminatedList(id, whName);
            
            if (apidata.length > 0) {
                if (apidata.length <= 9) {
                    setTerminatedList(apidata);
                    setTMainAPIResult(apidata);
                    setTerminatedListTemp(apidata);
                } else {
                    let userlistobj = JSON.parse(JSON.stringify(apidata));
                    let usertemplistobj = userlistobj.length > 9 ? userlistobj.splice(0, 9) : userlistobj;
                    setTerminatedList(apidata);
                    setTMainAPIResult(apidata);
                    setTerminatedListTemp(usertemplistobj);
                }
            }
            setTerminatedLoading(false);
        } catch (error) {
            setError('Error fetching terminated list! Please try again.');
            setTerminatedLoading(false);
        }
    }, []);

    const exportGroupNumberData = useCallback(async (item) => {
        try {
            const response = await fetch('/api/admin/Downlaod_GroupNumber_Data', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href
                },
                body: JSON.stringify({
                    whid: String(item.whId),
                    whname: item.whName
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const apidata = await response.json();

            if (!apidata || apidata.length === 0) {
                alert('No data available to export');
                return;
            }

            // Build CSV
            const tableData = ["Group Number,Commission,Termination Date"];

            // Check if first row is a header (contains "Group Number" text)
            const startIndex = (apidata[0]?.groupNumber === "Group Number" ||
                apidata[0]?.groupNumber?.toLowerCase().includes("group")) ? 1 : 0;

            // Skip first row if it's a header
            for (let i = startIndex; i < apidata.length; i++) {
                const row = apidata[i];
                const rowData = [
                    row.groupNumber || "",
                    row.commission || "",
                    row.terminationDate || ""
                ];
                tableData.push(rowData.join(","));
            }

            const csvData = tableData.join("\n");

            // Download CSV
            if (window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(new Blob([csvData]), "GroupNumber List.csv");
            } else {
                const blob = new Blob([csvData], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.download = "GroupNumber List.csv";
                link.href = url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            alert('Check internet connection. Unable to download group number data.');
            console.error('Export error:', error);
        }
    }, []);

    const exportDistributorData = useCallback(async (item) => {
        try {
            const response = await fetch('/api/admin/Downlaod_Distributor_Data', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href
                },
                body: JSON.stringify({
                    whid: String(item.whId),
                    whname: item.whName
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const apidata = await response.json();

            if (!apidata || apidata.length === 0) {
                alert('No data available to export');
                return;
            }

            // Build CSV
            const tableData = ["First Name,Last Name,Email,Commission"];

            // Check if first row is a header (contains "First Name" or "FirstName" text)
            const startIndex = (apidata[0]?.firstName === "First Name" ||
                apidata[0]?.firstName === "FirstName" ||
                apidata[0]?.firstName?.toLowerCase().includes("first")) ? 1 : 0;

            // Skip first row if it's a header
            for (let i = startIndex; i < apidata.length; i++) {
                const row = apidata[i];
                const rowData = [
                    row.firstName || "",
                    row.lastName || "",
                    row.emailId || "",
                    row.commission || ""
                ];
                tableData.push(rowData.join(","));
            }

            const csvData = tableData.join("\n");

            // Download CSV
            if (window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(new Blob([csvData]), "Distributor List.csv");
            } else {
                const blob = new Blob([csvData], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.download = "Distributor List.csv";
                link.href = url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            alert('Check internet connection. Unable to download distributor data.');
            console.error('Export error:', error);
        }
    }, []);

    useEffect(() => {
        if (userdata) {
            fetchTerminatedList(userdata.id, userdata.wholesaler);
            fetchDataLocally(userdata.paramsname, userdata.userrole);
        }
    }, [userdata, fetchDataLocally, fetchTerminatedList]);

    const handlePageChange = useCallback((pageNumber) => {
        let userlistobj = JSON.parse(JSON.stringify(userList));
        let usertemplistobj = userlistobj.length > 9 
            ? userlistobj.splice((pageNumber - 1) * 9, 9) 
            : userlistobj;
        setActivePage(pageNumber);
        setCurrentPaging(pageNumber);
        setUserListTemp(usertemplistobj);
    }, [userList]);

    const terminatedPageChange = useCallback((pageNumber) => {
        let userlistobj = JSON.parse(JSON.stringify(terminatedList));
        let usertemplistobj = userlistobj.length > 9 
            ? userlistobj.splice((pageNumber - 1) * 9, 9) 
            : userlistobj;
        setActivePage(pageNumber);
        setTerminatedListTemp(usertemplistobj);
    }, [terminatedList]);

    const handleSearch = useCallback(() => {
        let filteredList = mainAPIResult.filter(e1 =>
            e1.whName?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1 ||
            (e1.emailId && e1.emailId?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1) ||
            (e1.gnfix && e1.gnfix?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1)
        );

        if (filteredList.length === 0) {
            setDataNotFound('No records found');
        } else {
            setDataNotFound('');
        }

        let usertemplistobj = filteredList.length > 9 ? filteredList.splice(0, 9) : filteredList;
        setActivePage(1);
        setUserList(filteredList);
        setUserListTemp(usertemplistobj);
    }, [mainAPIResult, whSearchText]);

    const handleTerminatedSearch = useCallback(() => {
        let filteredList = tMainAPIResult.filter(e1 =>
            e1.whName?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1 ||
            (e1.emailId && e1.emailId?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1) ||
            (e1.gnfix && e1.gnfix?.toLowerCase()?.indexOf(whSearchText?.toLowerCase()) !== -1)
        );

        let usertemplistobj = filteredList.length > 9 ? filteredList.splice(0, 9) : filteredList;
        setActivePage(1);
        setTerminatedList(filteredList);
        setTerminatedListTemp(usertemplistobj);
    }, [tMainAPIResult, whSearchText]);

    const addWholesaler = () => {
        closePopup(currentPaging, whSearchText);
        closeAddWholesalerPopup(currentPaging);
    };

    const manageProfile = (item) => {
        closePopup(currentPaging, whSearchText);
        manageGroupPopup(item.whId, item.whName, currentPaging, 1, item.isRuleBased.toString(), whSearchText);
    };

    const manageEmail = (item) => {
        // Don't close ManageWholesaler, just open ManageEmail on top
        manageEmailPopup(item.dashBoardId, item.whId, item.whName, currentPaging, whSearchText);
    };

    const editProfile = (item) => {
        closePopup(currentPaging, whSearchText);
        closeEditProfilePopup(item.whName, item.whId, item.gnfix, currentPaging, whSearchText, item.isRuleBased);
    };

    const createDashboard = useCallback(async (item) => {
        try {
            setLoading(true);
            const apidata = await userService.AddWhDashUser(
                userdata.paramsname,
                userdata.userrole,
                item.whName,
                item.whId,
                item.whName, // userName - use wholesaler name
                item.emailId || item.whName // userEmail - use emailId or fallback to whName
            );
            
            if (apidata && apidata.length > 0 && apidata[0].status_code === "1") {
                // Refresh the wholesaler list after creating dashboard
                fetchDataLocally(userdata.paramsname, userdata.userrole);
            } else {

            }
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }, [userdata, fetchDataLocally]);

    const handleTabChange = (value) => {
        setTabSelected(value);
        setWhSearchText('');
        setActivePage(1);
    };

    return (
        <Dialog open={true} onOpenChange={() => closePopup(currentPaging, whSearchText)}>
            <DialogContent className="max-w-7xl h-[calc(100vh-3rem)]">
                <div className="flex flex-col h-full">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{text || 'Manage Wholesaler'}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex-1 flex justify-center items-center min-h-0">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="flex-shrink-0 space-y-4">
                                <Button onClick={addWholesaler} className="bg-primary hover:bg-primary/90">
                                    Add Wholesaler
                                </Button>

                                <div className="flex gap-4 items-center">
                                    <Input
                                        type="text"
                                        placeholder="Search"
                                        value={whSearchText}
                                        onChange={(e) => setWhSearchText(e.target.value)}
                                        onKeyUp={tabSelected === 'Active' ? handleSearch : handleTerminatedSearch}
                                        className="flex-1"
                                    />
                                    <Tabs value={tabSelected} onValueChange={handleTabChange} className="w-auto">
                                        <TabsList>
                                            <TabsTrigger value="Active">Active</TabsTrigger>
                                            <TabsTrigger value="Inactive">Terminated</TabsTrigger>
                                        </TabsList>
                                    </Tabs>
                                </div>
                            </div>

                            <Tabs value={tabSelected} onValueChange={handleTabChange} className="flex-1 flex flex-col min-h-0 mt-4">
                                <TabsContent value="Active" forceMount={false} className="flex-1 flex flex-col min-h-0 mt-0">
                                    {userList && userList.length > 0 ? (
                                        <>
                                            <div className="flex-1 overflow-y-auto min-h-0">
                                                <div className="border rounded-lg overflow-hidden">
                                                    <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>No. of Group Number</TableHead>
                                                        <TableHead>Group Prefix</TableHead>
                                                        <TableHead>Last Month Commission</TableHead>
                                                        <TableHead className="text-center">Action</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {userListTemp.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{item.whName}</TableCell>
                                                            <TableCell>{item.groupCount}</TableCell>
                                                            <TableCell>{item.gnfix}</TableCell>
                                                            <TableCell>{item.whComm}</TableCell>
                                                            <TableCell>
                                                                <TooltipProvider>
                                                                    <div className="flex gap-2 justify-center">
                                                                        {/* Group Number Icon */}
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <Button variant="ghost" size="sm" onClick={() => manageProfile(item)}>
                                                                                    <FileUser className="h-4 w-4" />
                                                                                </Button>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent>Group Number</TooltipContent>
                                                                        </Tooltip>
                                                                        
                                                                        {/* Show Create Dashboard if dashboardId is 0, otherwise show Manage Emails */}
                                                                        {parseInt(item.dashBoardId) === 0 ? (
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <Button 
                                                                                        variant="ghost" 
                                                                                        size="sm" 
                                                                                        onClick={() => createDashboard(item)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        <LayoutDashboard className="h-4 w-4" />
                                                                                    </Button>
                                                                                </TooltipTrigger>
                                                                                <TooltipContent>Create Dashboard</TooltipContent>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <Button variant="ghost" size="sm" onClick={() => manageEmail(item)}>
                                                                                        <Mail className="h-4 w-4" />
                                                                                    </Button>
                                                                                </TooltipTrigger>
                                                                                <TooltipContent>Manage Emails</TooltipContent>
                                                                            </Tooltip>
                                                                        )}
                                                                        
                                                                        {/* Manage Profile & Rules Icon */}
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <Button variant="ghost" size="sm" onClick={() => editProfile(item)}>
                                                                                    <UserCog className="h-4 w-4" />
                                                                                </Button>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent>Manage Profile & Rules</TooltipContent>
                                                                        </Tooltip>
                                                                    </div>
                                                                </TooltipProvider>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>

                                    {userList.length > 9 && (
                                        <div className="flex-shrink-0 flex justify-center mt-4">
                                            <Pagination
                                                activePage={activePage}
                                                itemsCountPerPage={9}
                                                totalItemsCount={userList.length}
                                                pageRangeDisplayed={5}
                                                onChange={handlePageChange}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <h3>{dataNotFound || 'No wholesaler found'}</h3>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="Inactive" forceMount={false} className="flex-1 flex flex-col min-h-0 mt-0">
                            {terminatedLoading ? (
                                <div className="flex-1 flex justify-center items-center min-h-0">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : terminatedList && terminatedList.length > 0 ? (
                                <>
                                    <div className="flex-1 overflow-y-auto min-h-0">
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>No. of Group Number</TableHead>
                                                        <TableHead>Group Prefix</TableHead>
                                                        <TableHead>Termination Date</TableHead>
                                                        <TableHead className="text-center">Action</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {terminatedListTemp.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{item.whName}</TableCell>
                                                            <TableCell>{item.groupCount}</TableCell>
                                                            <TableCell>{item.gnfix}</TableCell>
                                                            <TableCell>{item.terminationDate}</TableCell>
                                                            <TableCell className="text-center">
                                                                <div className="flex gap-2 justify-center">
                                                                    <TooltipProvider>
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={() => exportGroupNumberData(item)}
                                                                                >
                                                                                    <Download className="h-4 w-4" />
                                                                                </Button>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent>Download Group Numbers</TooltipContent>
                                                                        </Tooltip>
                                                                    </TooltipProvider>

                                                                    <TooltipProvider>
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={() => exportDistributorData(item)}
                                                                                >
                                                                                    <Download className="h-4 w-4" />
                                                                                </Button>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent>Download Distributor Data</TooltipContent>
                                                                        </Tooltip>
                                                                    </TooltipProvider>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>

                                    {terminatedList.length > 9 && (
                                        <div className="flex-shrink-0 flex justify-center mt-4">
                                            <Pagination
                                                activePage={activePage}
                                                itemsCountPerPage={9}
                                                totalItemsCount={terminatedList.length}
                                                pageRangeDisplayed={5}
                                                onChange={terminatedPageChange}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <h3>No terminated wholesalers</h3>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>

                    {error && (
                        <div className="flex-shrink-0 text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-4">
                            {error}
                        </div>
                    )}
                </>
            )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ManageWholesaler;
